import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { Separator } from '../ui/separator';
import { 
  MessageSquare, 
  Send, 
  AlertCircle, 
  RefreshCw, 
  Paperclip, 
  X, 
  Download, 
  FileText, 
  Image as ImageIcon,
  Search,
  CheckCheck,
  Check,
  Clock,
  AlertTriangle,
  Smile,
  ThumbsUp,
  Star,
  Filter,
  MoreVertical,
  User,
  Users
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from '../ui/dropdown-menu';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const QUICK_REPLIES = [
  "Thank you!",
  "I'll get back to you soon",
  "Could you provide more details?",
  "Yes, that works for me",
  "I'll schedule an appointment"
];

const EnhancedMessagingCenter = ({ userRole = 'patient' }) => {
  const [conversations, setConversations] = useState([]);
  const [filteredConversations, setFilteredConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [error, setError] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [showQuickReplies, setShowQuickReplies] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all'); // all, unread, urgent

  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadConversations();
    loadUnreadCount();
    
    // Poll for new messages every 30 seconds
    const interval = setInterval(() => {
      loadUnreadCount();
      if (selectedConversation) {
        loadMessages(selectedConversation.id, true);
      }
    }, 30000);
    
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    // Filter conversations based on search and filter status
    let filtered = conversations;
    
    if (searchQuery) {
      filtered = filtered.filter(conv => 
        conv.patient_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        conv.subject?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        conv.last_message?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    if (filterStatus === 'unread') {
      filtered = filtered.filter(conv => 
        userRole === 'patient' ? conv.unread_count_patient > 0 : conv.unread_count_staff > 0
      );
    } else if (filterStatus === 'urgent') {
      filtered = filtered.filter(conv => conv.is_urgent);
    }
    
    setFilteredConversations(filtered);
  }, [conversations, searchQuery, filterStatus, userRole]);

  useEffect(() => {
    // Auto-scroll to bottom when new messages arrive
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadConversations = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversations(response.data);
      setError('');
    } catch (err) {
      setError('Failed to load conversations');
      console.error(err);
      toast.error('Failed to load conversations');
    } finally {
      setLoading(false);
    }
  };

  const loadUnreadCount = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/messages/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadCount(response.data.unread_count);
    } catch (err) {
      console.error('Failed to load unread count:', err);
    }
  };

  const loadMessages = async (conversationId, silent = false) => {
    try {
      if (!silent) setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/conversations/${conversationId}/messages`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessages(response.data);
      setError('');
      
      // Refresh conversations to update unread counts
      await loadConversations();
      await loadUnreadCount();
    } catch (err) {
      setError('Failed to load messages');
      console.error(err);
      toast.error('Failed to load messages');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    const validFiles = files.filter(file => {
      const validTypes = ['image/jpeg', 'image/png', 'application/pdf', 
                         'application/msword', 
                         'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                         'application/vnd.ms-excel',
                         'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];
      const isValidType = validTypes.includes(file.type);
      const isValidSize = file.size <= 5 * 1024 * 1024; // 5MB
      
      if (!isValidType) {
        toast.error('Invalid file type. Allowed: JPG, PNG, PDF, DOCX, XLSX');
        return false;
      }
      if (!isValidSize) {
        toast.error('File too large. Maximum size: 5MB');
        return false;
      }
      return true;
    });
    
    if (validFiles.length > 0) {
      setSelectedFiles([...selectedFiles, ...validFiles]);
      toast.success(`${validFiles.length} file(s) selected`);
    }
  };

  const removeFile = (index) => {
    setSelectedFiles(selectedFiles.filter((_, i) => i !== index));
  };

  const uploadFiles = async () => {
    const token = localStorage.getItem('token');
    const uploadedAttachments = [];
    
    for (const file of selectedFiles) {
      const formData = new FormData();
      formData.append('file', file);
      
      try {
        const response = await axios.post(
          `${API_URL}/api/messages/upload`,
          formData,
          { 
            headers: { 
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            } 
          }
        );
        uploadedAttachments.push(response.data);
      } catch (err) {
        console.error('File upload failed:', err);
        toast.error(`Failed to upload ${file.name}`);
        throw err;
      }
    }
    
    return uploadedAttachments;
  };

  const sendMessage = async (messageText = null) => {
    const textToSend = messageText || newMessage;
    if ((!textToSend.trim() && selectedFiles.length === 0) || !selectedConversation) return;

    try {
      setSending(true);
      if (selectedFiles.length > 0) {
        setUploading(true);
      }
      const token = localStorage.getItem('token');
      
      // Upload files first if any
      let attachments = [];
      if (selectedFiles.length > 0) {
        attachments = await uploadFiles();
      }
      
      // Send message with attachments
      await axios.post(
        `${API_URL}/api/conversations/${selectedConversation.id}/messages`,
        { 
          message: textToSend || '(attachment)', 
          is_urgent: false,
          attachments: attachments
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setNewMessage('');
      setSelectedFiles([]);
      setShowQuickReplies(false);
      await loadMessages(selectedConversation.id);
      toast.success('Message sent successfully');
    } catch (err) {
      setError('Failed to send message');
      console.error(err);
      toast.error('Failed to send message');
    } finally {
      setSending(false);
      setUploading(false);
    }
  };

  const handleQuickReply = (reply) => {
    sendMessage(reply);
  };

  const startConversation = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Get member info for patient
      const userResponse = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const memberResponse = await axios.get(
        `${API_URL}/api/members/user/${userResponse.data.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const response = await axios.post(
        `${API_URL}/api/conversations`,
        {
          member_id: memberResponse.data.id,
          subject: 'New Conversation',
          message: 'Hello, I need assistance.',
          is_urgent: false
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      await loadConversations();
      setSelectedConversation(response.data);
      await loadMessages(response.data.id);
      toast.success('Conversation started');
    } catch (err) {
      setError('Failed to start conversation');
      console.error(err);
      toast.error('Failed to start conversation');
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const getDetailedTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getMessageStatusIcon = (msg) => {
    // For demo purposes - in production, you'd track actual delivery/read status
    const isOwnMessage = msg.sender_role === userRole;
    if (!isOwnMessage) return null;
    
    if (msg.is_read) {
      return <CheckCheck className="h-3 w-3 text-blue-400" />;
    }
    return <Check className="h-3 w-3 text-gray-400" />;
  };

  const getFileIcon = (fileType) => {
    if (fileType?.includes('image')) return <ImageIcon className="h-4 w-4" />;
    return <FileText className="h-4 w-4" />;
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-500 to-blue-500 flex items-center justify-center">
            <MessageSquare className="h-5 w-5 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Messages</h2>
            <p className="text-sm text-gray-600">
              {filteredConversations.length} conversation{filteredConversations.length !== 1 ? 's' : ''}
              {unreadCount > 0 && ` • ${unreadCount} unread`}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              loadConversations();
              if (selectedConversation) {
                loadMessages(selectedConversation.id);
              }
            }}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          {userRole === 'patient' && conversations.length === 0 && (
            <Button onClick={startConversation} className="bg-teal-600 hover:bg-teal-700">
              <MessageSquare className="h-4 w-4 mr-2" />
              Start Conversation
            </Button>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 flex items-center gap-2">
          <AlertCircle className="h-5 w-5 text-red-600" />
          <p className="text-red-800">{error}</p>
          <Button variant="ghost" size="sm" onClick={() => setError('')} className="ml-auto">
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}

      <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-4 overflow-hidden">
        {/* Conversations List */}
        <Card className="col-span-1 flex flex-col">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between mb-3">
              <CardTitle className="text-lg">Conversations</CardTitle>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm">
                    <Filter className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => setFilterStatus('all')}>
                    All Messages
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setFilterStatus('unread')}>
                    Unread Only
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setFilterStatus('urgent')}>
                    Urgent Only
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search conversations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
          </CardHeader>
          <CardContent className="p-0 flex-1 overflow-hidden">
            <ScrollArea className="h-full">
              {loading && conversations.length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mx-auto mb-2"></div>
                  <p className="text-sm">Loading...</p>
                </div>
              ) : filteredConversations.length === 0 ? (
                <div className="p-6 text-center text-gray-500">
                  <MessageSquare className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p className="font-medium mb-1">No conversations</p>
                  <p className="text-sm">
                    {searchQuery ? 'Try different search terms' : 'Start a new conversation'}
                  </p>
                  {userRole === 'patient' && !searchQuery && (
                    <Button onClick={startConversation} className="mt-4" size="sm">
                      Start Conversation
                    </Button>
                  )}
                </div>
              ) : (
                filteredConversations.map((conv) => {
                  const isUnread = userRole === 'patient' 
                    ? conv.unread_count_patient > 0 
                    : conv.unread_count_staff > 0;
                  const unreadCount = userRole === 'patient'
                    ? conv.unread_count_patient
                    : conv.unread_count_staff;
                  const isSelected = selectedConversation?.id === conv.id;

                  return (
                    <div
                      key={conv.id}
                      onClick={() => {
                        setSelectedConversation(conv);
                        loadMessages(conv.id);
                      }}
                      className={`p-4 border-b cursor-pointer hover:bg-gray-50 transition-all ${
                        isSelected 
                          ? 'bg-teal-50 border-l-4 border-l-teal-600' 
                          : isUnread 
                          ? 'bg-blue-50/50' 
                          : ''
                      }`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-400 to-blue-400 flex items-center justify-center flex-shrink-0">
                            {userRole === 'patient' ? (
                              <Users className="h-5 w-5 text-white" />
                            ) : (
                              <User className="h-5 w-5 text-white" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className={`font-medium truncate ${isUnread ? 'font-bold text-gray-900' : 'text-gray-700'}`}>
                              {userRole === 'patient' ? 'Clinic Staff' : conv.patient_name}
                            </h3>
                            {conv.subject && (
                              <p className="text-xs text-gray-500 truncate">{conv.subject}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1 ml-2">
                          <span className="text-xs text-gray-500 whitespace-nowrap">
                            {formatTime(conv.last_message_at)}
                          </span>
                          {isUnread && (
                            <Badge variant="default" className="text-xs bg-teal-600">
                              {unreadCount}
                            </Badge>
                          )}
                        </div>
                      </div>
                      <p className={`text-sm truncate ${isUnread ? 'font-semibold text-gray-900' : 'text-gray-600'}`}>
                        {conv.last_message || 'No messages yet'}
                      </p>
                    </div>
                  );
                })
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Messages Area */}
        <Card className="col-span-1 md:col-span-2 flex flex-col">
          <CardHeader className="pb-3">
            {selectedConversation ? (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-400 to-blue-400 flex items-center justify-center">
                    {userRole === 'patient' ? (
                      <Users className="h-5 w-5 text-white" />
                    ) : (
                      <User className="h-5 w-5 text-white" />
                    )}
                  </div>
                  <div>
                    <CardTitle className="text-lg">
                      {userRole === 'patient' 
                        ? 'Conversation with Clinic' 
                        : selectedConversation.patient_name}
                    </CardTitle>
                    {selectedConversation.subject && (
                      <CardDescription className="text-sm">{selectedConversation.subject}</CardDescription>
                    )}
                  </div>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => loadMessages(selectedConversation.id)}>
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Refresh Messages
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem>
                      <AlertTriangle className="h-4 w-4 mr-2" />
                      Mark as Urgent
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ) : (
              <CardTitle className="text-lg">Select a conversation</CardTitle>
            )}
          </CardHeader>
          <CardContent className="flex-1 flex flex-col p-0 overflow-hidden">
            {!selectedConversation ? (
              <div className="flex-1 flex items-center justify-center text-gray-500 p-6">
                <div className="text-center">
                  <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                    <MessageSquare className="h-10 w-10 text-gray-300" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No Conversation Selected</h3>
                  <p className="text-sm text-gray-600">
                    Choose a conversation from the list to start messaging
                  </p>
                </div>
              </div>
            ) : (
              <>
                {/* Messages List */}
                <ScrollArea className="flex-1 px-6 py-4">
                  <div className="space-y-4">
                    {messages.length === 0 ? (
                      <div className="text-center text-gray-500 py-12">
                        <MessageSquare className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                        <p>No messages yet. Start the conversation!</p>
                      </div>
                    ) : (
                      messages.map((msg, index) => {
                        const isOwnMessage = msg.sender_role === userRole;
                        const showDateSeparator = index === 0 || 
                          new Date(msg.created_at).toDateString() !== new Date(messages[index - 1].created_at).toDateString();
                        
                        return (
                          <React.Fragment key={msg.id}>
                            {showDateSeparator && (
                              <div className="flex items-center gap-3 my-4">
                                <Separator className="flex-1" />
                                <span className="text-xs text-gray-500 font-medium">
                                  {new Date(msg.created_at).toLocaleDateString('en-US', {
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric'
                                  })}
                                </span>
                                <Separator className="flex-1" />
                              </div>
                            )}
                            
                            <div className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'} group`}>
                              <div className={`max-w-[75%] ${isOwnMessage ? 'order-2' : 'order-1'}`}>
                                <div
                                  className={`rounded-2xl px-4 py-3 ${
                                    isOwnMessage
                                      ? 'bg-gradient-to-br from-teal-600 to-blue-500 text-white rounded-tr-md'
                                      : 'bg-gray-100 text-gray-900 rounded-tl-md'
                                  }`}
                                >
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className={`text-xs font-medium ${isOwnMessage ? 'text-teal-100' : 'text-gray-600'}`}>
                                      {msg.sender_name}
                                    </span>
                                    {msg.is_urgent && (
                                      <Badge variant="destructive" className="text-xs py-0 px-1 h-5">
                                        <AlertTriangle className="h-3 w-3 mr-1" />
                                        Urgent
                                      </Badge>
                                    )}
                                  </div>
                                  
                                  <p className="text-sm whitespace-pre-wrap break-words">{msg.message}</p>
                                  
                                  {/* Attachments */}
                                  {msg.attachments && msg.attachments.length > 0 && (
                                    <div className="mt-3 space-y-2">
                                      {msg.attachments.map((att, idx) => (
                                        <div
                                          key={idx}
                                          className={`flex items-center gap-2 p-2 rounded-lg ${
                                            isOwnMessage ? 'bg-teal-700/30' : 'bg-gray-200'
                                          }`}
                                        >
                                          {getFileIcon(att.file_type)}
                                          <span className="text-xs flex-1 truncate">{att.filename}</span>
                                          <Button
                                            size="sm"
                                            variant="ghost"
                                            className={`h-6 w-6 p-0 ${isOwnMessage ? 'hover:bg-teal-700/50' : 'hover:bg-gray-300'}`}
                                            onClick={() => window.open(`${API_URL}${att.file_path}`, '_blank')}
                                          >
                                            <Download className="h-3 w-3" />
                                          </Button>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                  
                                  <div className={`flex items-center gap-2 mt-2 ${isOwnMessage ? 'justify-end' : 'justify-start'}`}>
                                    <span className={`text-xs ${isOwnMessage ? 'text-teal-100' : 'text-gray-500'}`}>
                                      {new Date(msg.created_at).toLocaleTimeString('en-US', {
                                        hour: '2-digit',
                                        minute: '2-digit'
                                      })}
                                    </span>
                                    {getMessageStatusIcon(msg)}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </React.Fragment>
                        );
                      })
                    )}
                    <div ref={messagesEndRef} />
                  </div>
                </ScrollArea>

                {/* Quick Replies */}
                {showQuickReplies && (
                  <div className="px-6 py-2 border-t bg-gray-50">
                    <div className="flex items-center gap-2 overflow-x-auto pb-2">
                      <span className="text-xs text-gray-600 whitespace-nowrap">Quick replies:</span>
                      {QUICK_REPLIES.map((reply, idx) => (
                        <Button
                          key={idx}
                          variant="outline"
                          size="sm"
                          className="whitespace-nowrap text-xs h-7"
                          onClick={() => handleQuickReply(reply)}
                        >
                          {reply}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}

                {/* File Preview */}
                {selectedFiles.length > 0 && (
                  <div className="px-6 py-3 border-t bg-blue-50">
                    <div className="flex items-center gap-2 mb-2">
                      <Paperclip className="h-4 w-4 text-blue-600" />
                      <span className="text-sm font-medium text-blue-900">
                        {selectedFiles.length} file{selectedFiles.length !== 1 ? 's' : ''} attached
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {selectedFiles.map((file, idx) => (
                        <div
                          key={idx}
                          className="flex items-center gap-2 bg-white border border-blue-200 rounded-lg px-3 py-2"
                        >
                          {getFileIcon(file.type)}
                          <span className="text-xs text-gray-700 max-w-[150px] truncate">
                            {file.name}
                          </span>
                          <button
                            onClick={() => removeFile(idx)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Message Input */}
                <div className="p-4 border-t bg-white">
                  <div className="flex gap-2">
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept="image/jpeg,image/png,application/pdf,.doc,.docx,.xls,.xlsx"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      title="Attach files"
                    >
                      <Paperclip className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setShowQuickReplies(!showQuickReplies)}
                      title="Quick replies"
                    >
                      <Smile className="h-4 w-4" />
                    </Button>
                    <Textarea
                      placeholder="Type your message..."
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          sendMessage();
                        }
                      }}
                      className="flex-1 min-h-[44px] max-h-[120px] resize-none"
                      rows={1}
                    />
                    <Button
                      onClick={() => sendMessage()}
                      disabled={sending || (!newMessage.trim() && selectedFiles.length === 0)}
                      className="bg-teal-600 hover:bg-teal-700"
                      size="icon"
                    >
                      {sending ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      ) : (
                        <Send className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Press Enter to send, Shift + Enter for new line
                  </p>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default EnhancedMessagingCenter;
